import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import type { PartnerLimitV2, PartnerLimitCreateV2 } from '@shipium-corp/shipium-ts-types';
import type { V2LimitUpdatePayload } from '~/store/services/LimitService/types';
import {
  ALL_PARTNER_LIMITS_QUERY_KEY,
  DOCK_LANE_LIMITS_QUERY_KEY,
  DOCK_LANE_SHIPMENT_COUNTS_QUERY_KEY,
} from '~/hooks/queries/dockLaneQueryKeys';
import { LIMITS_BASE } from './limit-constants';

// PartnerLimitCreateV2 makes laneId optional because the type is shared with non-lane limits.
// These mutations are only for lane-scoped limits, so we require it here — the invalidation
// key needs it and a missing laneId would silently no-op.
type LaneLimitCreatePayload = PartnerLimitCreateV2 & { laneId: string };
type LaneLimitUpdatePayload = V2LimitUpdatePayload & { laneId: string };
export type LaneLimit = PartnerLimitV2 & { laneId: string };

// Per-lane drawer, partner-wide grouped table, and the timeline's segment counts all need
// refreshing after a write; missing any leaves one stale.
const invalidateForLane = (laneId: string) => [
  [DOCK_LANE_LIMITS_QUERY_KEY, laneId],
  [ALL_PARTNER_LIMITS_QUERY_KEY],
  [DOCK_LANE_SHIPMENT_COUNTS_QUERY_KEY],
];

export const laneLimitMutations = {
  create: async (payload: LaneLimitCreatePayload) => {
    const { data } = await GatewayAxiosClient.post<PartnerLimitV2>(LIMITS_BASE, payload);
    return {
      data,
      invalidateQueryKeys: invalidateForLane(payload.laneId),
    };
  },

  update: async (payload: LaneLimitUpdatePayload) => {
    const { data } = await GatewayAxiosClient.put<PartnerLimitV2>(
      `${LIMITS_BASE}/${encodeURIComponent(payload.limitId)}`,
      payload,
    );
    return {
      data,
      invalidateQueryKeys: invalidateForLane(payload.laneId),
    };
  },

  // Soft delete via update with archive: true — matches LimitV2 deletion semantics.
  archive: async (limit: LaneLimit) => {
    const payload: LaneLimitUpdatePayload = {
      active: limit.active,
      archive: true,
      constraints: limit.constraints,
      effectiveDate: limit.effectiveDate,
      expirationDate: limit.expirationDate,
      laneId: limit.laneId,
      limitId: limit.limitId,
      localCutoffTime: limit.localCutoffTime,
      localZoneId: limit.localZoneId,
      name: limit.name,
    };
    const { data } = await GatewayAxiosClient.put<PartnerLimitV2>(
      `${LIMITS_BASE}/${encodeURIComponent(limit.limitId)}`,
      payload,
    );
    return {
      data,
      invalidateQueryKeys: invalidateForLane(limit.laneId),
    };
  },
};
